import React from 'react';
import {StyleSheet, findNodeHandle, Platform} from 'react-native';
import {Heading, Box, Center} from 'native-base';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
  FieldError,
} from 'react-hook-form';
import {
  DateInput as Date,
  FileInput as File,
  MaskedInput,
  TextArea,
} from '../../../../../components/inputs';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/buttons';
import {ITaxForm} from '../../../../../interfaces/forms';
import {checkIsValidFile} from '../../../../../utils/validations';
import useToast from '../../../../../hooks';
import {ProofType} from '../../../../../constants/enumerations';
import {message} from '../../../../../utils/messages';
import {isWebApp} from '../../../../../utils/platform/is-web';
import {ResultOfRequestCached} from '../../../../../interfaces/types/result-of-request-cached';

interface FormProps {
  onSubmit?: (
    transaction: ITaxForm,
    type: ProofType,
  ) => Promise<boolean | ResultOfRequestCached>;
  onEndSubmit?: (success: boolean | ResultOfRequestCached) => void;
}

const VatTaxForm = ({
  onSubmit: launchSubmitEvent,
  onEndSubmit: launchEndSubmitEvent,
}: FormProps) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      amount: undefined,
      date: undefined,
      bank: undefined,
      voucher: undefined,
      file: undefined,
    } as ITaxForm,
  });

  const {showInputsError} = useToast();

  const onSubmit: SubmitHandler<ITaxForm> = async (data: ITaxForm) => {
    if (launchSubmitEvent) {
      const success = await launchSubmitEvent(data, ProofType.vatTax);
      if (
        success !== false &&
        (success as ResultOfRequestCached).status !== 'fail'
      ) {
        handleClearForm();
      }
      if (launchEndSubmitEvent) {
        launchEndSubmitEvent(success);
      }
    }
  };

  const onInvalid: SubmitErrorHandler<ITaxForm> = async (
    fieldErrors: FieldErrors<ITaxForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleDateSelect = () => {};

  const handleClearForm = () => {
    reset();
  };

  const awareExtraConfiguration: any =
    Platform.OS === 'ios'
      ? {
          extraHeight: 100,
          extraScrollHeight: 100,
          innerRef: (ref: any) => {
            (this as any).scroll = ref;
          },
        }
      : {};

  const textAreaExtraConfiguration: any =
    Platform.OS === 'ios'
      ? {
          onContentSizeChange: (event: any) => {
            var that: any = this;
            if (that && that.scrollRef && that.scrollRef.props) {
              that.scrollRef.props.scrollToFocusedInput(
                findNodeHandle(event.target),
              );
            }
          },
        }
      : {};

  return (
    <Box flex={1}>
      <KeyboardAwareScrollView
        style={styles.content}
        enableOnAndroid={true}
        scrollEnabled={true}
        enableAutomaticScroll={true}
        showsVerticalScrollIndicator={isWebApp}
        {...awareExtraConfiguration}>
        <Center width="100%" flex={1} justifyContent="flex-start">
          <Box width="100%" py={5}>
            <Heading
              size="xs"
              paddingBottom="3"
              fontWeight="bold"
              color="#06355E">
              {message('proof-voucher-subtitle-payment')}
            </Heading>
            <MaskedInput
              control={control}
              name="amount"
              label="proof-label-input-amount"
              mask="money"
              error={errors.amount}
              rules={{
                required: 'form-is-required',
              }}
            />
            <Date
              control={control}
              name="date"
              label="proof-label-input-date"
              onConfirm={handleDateSelect}
              error={errors.date}
              rules={{
                required: 'form-is-required',
              }}
            />
            <MaskedInput
              control={control}
              name="voucher"
              label="proof-label-input-voucher"
              mask="voucher"
              error={errors.voucher}
              rules={{
                required: 'form-is-required',
              }}
            />
            <TextArea
              control={control}
              name="comments"
              label="proof-label-input-comment"
              error={errors.comments}
              numberOfLines={4}
              maxLength={250}
              {...textAreaExtraConfiguration}
              rules={{
                required: 'form-is-required',
              }}
            />
            <File
              control={control}
              name="file"
              label="proof-label-input-voucher-file"
              info={{
                before: 'proof-info-input-voucher-file-before',
                after: 'proof-info-input-voucher-file-after',
              }}
              error={errors?.file as FieldError}
              rules={{
                required: 'form-is-required',
                validate: checkIsValidFile,
              }}
              style={styles.file}
            />
            <Box mt={5}>
              <PrimaryButton
                label="sign-up-proof-send"
                onPress={handleSubmit(onSubmit, onInvalid)}
              />
            </Box>
            <Box mt={5}>
              <SecondaryButton
                label="proof-button-cancel"
                onPress={handleClearForm}
              />
            </Box>
          </Box>
        </Center>
      </KeyboardAwareScrollView>
    </Box>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  file: {
    marginTop: 15,
  },
});

export default VatTaxForm;
